import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { collectionInputs } from '@ayumia/component-runtime';
import { routeOfTarget } from '../app/router';
import { Icon } from '../design-system/icons';
import type { TagSuggestion } from '../design-system/TagInput';
import { useToast } from '../design-system/Toast';
import { NOT_PERMITTED, useCommandAccess } from '../views/collection/command-access';
import { FolderCollection, type InfoTarget } from '../views/collection/folders/FolderCollection';
import { queryFolderSource, type Listing } from '../views/collection/folders/folder-source';
import type { ItemPresenter } from '../views/collection/folders/FolderViews';
import { useImageLinks } from '../views/image-links';
import { useAppServices } from '../views/ViewContext';
import { dateLabel, dateTimeLabel, dimensionsLabel, documentKind, extensionOf, LABELS, purposeFor, sha256, sizeLabel, type FileEntry } from './media-format';
import { DocumentPanel, FolderPanel, ImagePanel } from './MediaPanels';
import { mediaView, type Library } from './media-views';
import './media.css';

/**
 * Médiathèque (spec 192 §13, ADR-106 ; ADR-149 §6) : une **vue** sur le composant de collection, en
 * présentation drive — bouton Nouveau, raccourcis, recherche en pilule, filtres en puces, dossiers en
 * tête puis fichiers, sélection par clic sur la carte, barre de sélection, panneau ⓘ. La page n'apporte
 * que son extension : dépôt (`RequestFileUpload`, `CompleteFileUpload`), vignettes, panneaux image et
 * document, usage du dépôt selon le dossier système. Tout passe par des commandes : le serveur
 * autorise, valide et refuse ; ranger ne change aucun lien ni aucune clé de stockage.
 */

interface UploadState {
  name: string;
  status: 'pending' | 'done' | 'error';
  message?: string;
}

const messageOf = (error: unknown): string => (error instanceof Error ? error.message : String(error));

export function MediaLibraryPage({ library, folderId }: { library: Library; folderId?: string }) {
  const services = useAppServices();
  const client = services.client;
  const labels = LABELS[library];
  const toast = useToast();
  const view = useMemo(() => mediaView(library), [library]);
  const spec = collectionInputs(view).folders!;
  const source = useMemo(() => queryFolderSource<FileEntry>(client, spec), [client, spec]);
  const [listing, setListing] = useState<Listing<FileEntry> | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = useCallback(() => setRefreshKey((value) => value + 1), []);
  const [uploads, setUploads] = useState<UploadState[]>([]);
  const [libraryTags, setLibraryTags] = useState<TagSuggestion[]>([]);
  const fileInput = useRef<HTMLInputElement>(null);
  // Déposer est une commande comme les autres : refusée d'avance, elle est grisée avec son motif.
  const canUpload = useCommandAccess(client, ['RequestFileUpload']).allowed('RequestFileUpload');
  const pick = (): void => {
    if (canUpload) fileInput.current?.click();
  };

  // Vocabulaire d'étiquettes de cette médiathèque : propositions des panneaux, relu après chaque changement.
  useEffect(() => {
    let alive = true;
    client
      .query<{ tags: TagSuggestion[] }>('ListMediaTags', { library, limit: '200' })
      .then((response) => alive && setLibraryTags(response.data.tags))
      .catch(() => alive && setLibraryTags([]));
    return () => {
      alive = false;
    };
  }, [client, library, refreshKey]);

  const files = listing?.items ?? [];
  const thumbs = useImageLinks(client, library === 'images' ? files.map((file) => file.id) : [], 'square_200');

  const save = async (name: string, input: { payload: Record<string, unknown>; target?: { type: string; id: string } }, done: string): Promise<boolean> => {
    try {
      await client.command(name, input);
      toast('success', done);
      refresh();
      return true;
    } catch (error) {
      toast('danger', messageOf(error));
      return false;
    }
  };

  const uploadAll = async (list: readonly File[]): Promise<void> => {
    if (list.length === 0) return;
    const folder = listing?.folder ?? null;
    const purpose = purposeFor(library, folder === null ? null : String(folder.raw['system_key'] ?? '') || null);
    setUploads(list.map((file) => ({ name: file.name, status: 'pending' })));
    let stored = 0;
    for (const [index, file] of list.entries()) {
      const mark = (state: Partial<UploadState>): void => setUploads((current) => current.map((entry, at) => (at === index ? { ...entry, ...state } : entry)));
      try {
        const requested = await client.command<{ result: { file_id: string; upload: { url: string; method: string; headers?: Record<string, string> } } }>('RequestFileUpload', {
          payload: { file_name: file.name, media_type: file.type === '' ? 'application/octet-stream' : file.type, size_bytes: file.size, sha256: await sha256(file), purpose },
        });
        await client.upload(requested.result.upload, file);
        const completed = await client.command<{ result: { lifecycle_state: string; quarantine_reason?: string } }>('CompleteFileUpload', { payload: {}, target: { type: 'File', id: requested.result.file_id } });
        if (completed.result.lifecycle_state !== 'available') {
          mark({ status: 'error', message: `Refusé (${completed.result.quarantine_reason ?? 'contrôle du fichier'})` });
          continue;
        }
        if (folder !== null) await client.command('MoveFiles', { payload: { file_ids: [requested.result.file_id], folder_id: folder.id } });
        mark({ status: 'done' });
        stored += 1;
      } catch (error) {
        mark({ status: 'error', message: messageOf(error) });
      }
    }
    toast(stored === list.length ? 'success' : 'danger', `${stored} ${stored > 1 ? labels.many : labels.one} déposé${labels.e}${stored > 1 ? 's' : ''} sur ${list.length}.`);
    refresh();
  };

  const presenter: ItemPresenter<FileEntry> = {
    id: (file) => file.id,
    title: (file) => file.title,
    badge: (file) => (library === 'images' ? { text: extensionOf(file), tone: 'warning' } : { text: extensionOf(file), tone: documentKind(file).tone }),
    thumbnail: (file) => thumbs.get(file.id),
    meta: (file) => (library === 'images' ? [dimensionsLabel(file), sizeLabel(file.size_bytes)].filter(Boolean).join(' · ') : `Déposé le ${dateTimeLabel(file.created_at)} · ${sizeLabel(file.size_bytes)}`),
    cell: (field, file) => {
      if (field === 'dimensions') return dimensionsLabel(file) ?? '—';
      if (field === 'size_bytes') return sizeLabel(file.size_bytes);
      if (field === 'tags') return file.tags.join(', ');
      if (field === 'format') return documentKind(file).format;
      if (field === 'page_count') return file.page_count ?? '—';
      if (field === 'created_at') return library === 'images' ? dateLabel(file.created_at) : dateTimeLabel(file.created_at);
      return '—';
    },
    menu: (file) => [
      { id: 'download', label: 'Télécharger', icon: 'download', onSelect: () => void downloadAll(client, [file.id]) },
      ...(library === 'images' ? [{ id: 'edit', label: 'Éditer l’image', icon: 'pencil', onSelect: () => undefined, disabled: true, hint: 'Recadrage, rotation, détourage : prochaine version' }] : []),
    ],
  };

  const place = (): string => [labels.title, ...(listing?.path ?? []).map((crumb) => crumb.name)].join(' › ');
  const info = (target: InfoTarget<FileEntry>, controls: { close: () => void; openFolder: (id: string) => void; rename: (folder: NonNullable<Listing<FileEntry>['folder']>) => void; move: (item: FileEntry) => void }) => {
    if (target.kind === 'folder') {
      const folder = target.folder;
      return <FolderPanel key={folder.id} folder={folder} library={library} place={target.place} onClose={controls.close} onOpen={() => controls.openFolder(folder.id)} onRename={() => controls.rename(folder)} onSave={(description) => save('UpdateMediaFolderProperties', { payload: { description }, target: { type: 'MediaFolder', id: folder.id } }, 'Description enregistrée.')} />;
    }
    const file = target.item;
    const update = (patch: Record<string, unknown>) => save('UpdateFileProperties', { payload: patch, target: { type: 'File', id: file.id } }, 'Propriétés enregistrées.');
    return library === 'images' ? (
      <ImageDetails key={file.id} file={file} thumb={thumbs.get(file.id)} suggestions={libraryTags} onClose={controls.close} onSave={update} />
    ) : (
      <DocumentDetails key={file.id} file={file} place={place()} suggestions={libraryTags} onClose={controls.close} onMove={() => controls.move(file)} onSave={update} />
    );
  };

  return (
    <div className={`ay-media ay-media--${library}`}>
      <input ref={fileInput} type="file" multiple accept={labels.accept} hidden onChange={(event) => { const list = [...(event.target.files ?? [])]; event.target.value = ''; void uploadAll(list); }} aria-label={`Choisir des ${labels.many}`} />
      <FolderCollection<FileEntry>
        key={library}
        view={view}
        viewKey={`media.${library}`}
        client={client}
        source={source}
        presenter={presenter}
        labels={{ root: labels.title, items: labels.title, one: labels.one, many: labels.many, e: labels.e, search: labels.search, open: labels.open }}
        folderId={folderId ?? null}
        onNavigate={(id) => services.navigate({ kind: 'media', library, ...(id === null ? {} : { folder_id: id }) })}
        onNavigateWorkspace={(ref) => services.navigate(routeOfTarget({ type: 'workspace', ref }))}
        refreshKey={refreshKey}
        newEntries={[{ id: 'upload', label: `Déposer des ${labels.many}`, icon: 'upload', onSelect: pick, ...(canUpload ? {} : { disabled: true, reason: NOT_PERMITTED }) }]}
        {...(canUpload ? { onDropFiles: (list: File[]) => void uploadAll(list) } : {})}
        onListing={setListing}
        notice={
          uploads.length > 0 ? (
            <ul className="ay-media__uploads" aria-label="Dépôts en cours">
              {uploads.map((entry, index) => (
                <li key={`${entry.name}-${index}`} className={`ay-media__upload ay-media__upload--${entry.status}`}>
                  <span>{entry.name}</span>
                  <span>{entry.status === 'pending' ? 'Dépôt…' : entry.status === 'done' ? 'Déposé' : entry.message}</span>
                </li>
              ))}
              {uploads.every((entry) => entry.status !== 'pending') && <li><button type="button" className="ay-media__link" onClick={() => setUploads([])}>Effacer la liste</button></li>}
            </ul>
          ) : undefined
        }
        empty={(searching) => (
          <button type="button" className="ay-media__dropzone" aria-disabled={canUpload ? undefined : true} title={canUpload ? undefined : NOT_PERMITTED} onClick={pick}>
            <Icon name="upload" size={26} />
            <span>{searching ? 'Aucun résultat.' : `${labels.e === 'e' ? 'Aucune' : 'Aucun'} ${labels.one} ici. Glissez des fichiers ou cliquez pour déposer.`}</span>
            <small>{canUpload ? labels.hint : NOT_PERMITTED}</small>
          </button>
        )}
        trailing={
          <button type="button" className="ay-media__dropzone ay-media__dropzone--tile" aria-disabled={canUpload ? undefined : true} title={canUpload ? undefined : NOT_PERMITTED} onClick={pick}>
            <Icon name="upload" size={26} />
            <span>Déposer ici</span>
            <small>{canUpload ? labels.hint : NOT_PERMITTED}</small>
          </button>
        }
        extraActions={(chosen, everything) => [
          { id: 'download', label: 'Télécharger', icon: 'download', disabled: everything || chosen.length === 0, ...(everything ? { reason: 'Le téléchargement vise les fichiers choisis un à un.' } : chosen.length === 0 ? { reason: 'Choisissez des fichiers.' } : {}), onRun: () => void downloadAll(client, chosen.map((file) => file.id)) },
        ]}
        info={info}
        trail={[{ label: 'Accueil', onSelect: () => services.navigate({ kind: 'home' }) }, { label: 'Médiathèque' }]}
        subtitle={(folder) => folder?.description ?? (folder?.locked === true ? labels.system : labels.description)}
      />
    </div>
  );
}

/** Image ouverte : sa variante « détail » servie en plus de la vignette. */
function ImageDetails({ file, thumb, suggestions, onClose, onSave }: { file: FileEntry; thumb: string | undefined; suggestions: readonly TagSuggestion[]; onClose: () => void; onSave: (patch: Record<string, unknown>) => Promise<boolean> }) {
  const services = useAppServices();
  const previews = useImageLinks(services.client, [file.id], 'detail');
  return <ImagePanel file={file} preview={previews.get(file.id) ?? thumb} suggestions={suggestions} onClose={onClose} onSave={onSave} />;
}

function DocumentDetails({ file, place, suggestions, onClose, onMove, onSave }: { file: FileEntry; place: string; suggestions: readonly TagSuggestion[]; onClose: () => void; onMove: () => void; onSave: (patch: Record<string, unknown>) => Promise<boolean> }) {
  const services = useAppServices();
  const previews = useImageLinks(services.client, file.media_type.startsWith('image/') ? [file.id] : [], 'detail');
  return <DocumentPanel file={file} imagePreview={previews.get(file.id)} place={place} suggestions={suggestions} onClose={onClose} onMove={onMove} onSave={onSave} />;
}

/** Ouvre le lien signé de chaque fichier choisi (un onglet par fichier, comme le v1). */
async function downloadAll(client: ReturnType<typeof useAppServices>['client'], ids: readonly string[]): Promise<void> {
  for (const id of ids) {
    const response = await client.query<{ download: { url: string } }>('GetFileDownload', { file_id: id });
    globalThis.open?.(response.data.download.url, '_blank', 'noopener');
  }
}
